import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Alert, Col, Form, InputGroup, Row } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import Button from 'components/base/Button';
import AuthSimpleLayout from 'layouts/AuthSimpleLayout';
import { register } from 'features/auth/authApi';
import { UserRole } from 'features/auth/authSession';
import {
  Coordinates,
  geocodeAddress,
  reverseGeocode
} from 'features/location/googleLocation';
import LocationAutocomplete from 'components/common/LocationAutocomplete';
import PasswordCriteria from 'components/forms/PasswordCriteria';

const PHARMACY_DOCUMENT_FIELDS = [
  {
    key: 'passport',
    label: 'Passport',
    required: true
  },
  {
    key: 'visa_work_permit',
    label: 'Visa/Work permit',
    required: false
  },
  {
    key: 'enhanced_dbs_check',
    label:
      'Enhanced DBS Check (we can help you arrange this if required - please contact us)',
    required: false
  },
  {
    key: 'safeguarding_level_2',
    label: 'Safeguarding level 2',
    required: false
  },
  {
    key: 'gphc_number_doc',
    label: 'GPhC Number',
    required: true
  },
  {
    key: 'smartcard',
    label: 'Smartcard',
    required: true
  }
] as const;

const LOCUM_DOCUMENT_FIELDS = [
  {
    key: 'passport',
    label: 'Passport',
    required: true
  },
  {
    key: 'visa_work_permit',
    label: 'Visa/Work permit',
    required: false
  },
  {
    key: 'safeguarding_level_2',
    label: 'Safeguarding level 2',
    required: true
  },
  {
    key: 'qualification_certificates',
    label: 'Qualification Certificates',
    required: true
  }
] as const;

const PHARMACIST_LOCUM_DOCUMENTS = [
  {
    key: 'dbs_check',
    label: 'DBS Check',
    required: true
  }
] as const;

const PHARMACIST_TERMS_URL = 'https://www.capitallocums.co.uk/pharmacisttandcs';
const PHARMACIST_PRIVACY_URL = 'https://www.capitallocums.co.uk/privacy-policy';
const LOCUM_TERMS_URL = 'https://www.capitallocums.co.uk/dispenser-technician-tcs';
const LOCUM_PRIVACY_URL = 'https://www.capitallocums.co.uk/privacy-policy';

type PharmacyDocumentKey = (typeof PHARMACY_DOCUMENT_FIELDS)[number]['key'];
type LocumDocumentKey = (typeof LOCUM_DOCUMENT_FIELDS)[number]['key'];
type PharmacistLocumDocumentKey = (typeof PHARMACIST_LOCUM_DOCUMENTS)[number]['key'];
type DocumentKey = PharmacyDocumentKey | LocumDocumentKey | PharmacistLocumDocumentKey;

const renderDocumentLabel = (field: {
  key: DocumentKey;
  label: string;
  required: boolean;
}) => {
  if (field.key === 'enhanced_dbs_check') {
    return (
      <>
        <span>Enhanced DBS Check</span>
        <span className="d-block text-body-tertiary fs-10">
          (we can help you arrange this if required - please contact us)
        </span>
      </>
    );
  }

  if (field.key === 'visa_work_permit') {
    return (
      <>
        <span>Visa/Work permit</span>
        <span className="d-block text-body-tertiary fs-10">(if required)</span>
      </>
    );
  }

  return (
    <span>
      {field.label} {field.required && <span className="text-danger">*</span>}
    </span>
  );
};

const UK_PHONE_LOCAL_REGEX = /^\d{9,10}$/;

const normalizeUkPhoneLocal = (value: string) => {
  const digits = value.replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('44')) {
    return digits.slice(2);
  }
  if (digits.startsWith('0')) {
    return digits.slice(1);
  }
  return digits;
};

const formatUkPhone = (value: string) => {
  const digits = normalizeUkPhoneLocal(value);
  return digits ? `+44${digits}` : '';
};

const RegisterPage = () => {
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [last_name, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [location, setLocation] = useState('');
  const [role, setRole] = useState<UserRole>('locum');

  const [phone, setPhone] = useState('');
  const [qualifications, setQualifications] = useState('');
  const [experienceYears, setExperienceYears] = useState('');
  const [travelDistance, setTravelDistance] = useState('');
  const [locumRole, setLocumRole] = useState<'pharmacist' | 'technician' | 'dispenser'>(
    'pharmacist'
  );
  const [gphcNumber, setGphcNumber] = useState('');
  const [nationalInsuranceNumber, setNationalInsuranceNumber] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [dob, setDob] = useState('');
  const [gender, setGender] = useState<'male' | 'female' | 'other' | ''>('');
  const [qualificationDate, setQualificationDate] = useState('');
  const [independentPrescriber, setIndependentPrescriber] = useState<string>('');
  const [acceptedIr35Compliance, setAcceptedIr35Compliance] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);

  const [pharmacyName, setPharmacyName] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [pharmacyPhone, setPharmacyPhone] = useState('');
  const [pharmacyAddress, setPharmacyAddress] = useState('');
  const [dispensingSystem, setDispensingSystem] = useState('');
  const [licenseNumber, setLicenseNumber] = useState('');

  const [refName1, setRefName1] = useState('');
  const [refPhone1, setRefPhone1] = useState('');
  const [refDetails1, setRefDetails1] = useState('');
  const [refName2, setRefName2] = useState('');
  const [refPhone2, setRefPhone2] = useState('');
  const [refDetails2, setRefDetails2] = useState('');

  const [documentFiles, setDocumentFiles] = useState<
    Partial<Record<DocumentKey, File | null>>
  >({});
  const [qualificationCertificates, setQualificationCertificates] = useState<File[]>([]);

  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);
  const [detectedCoordinates, setDetectedCoordinates] =
    useState<Coordinates | null>(null);

  const isLocum = useMemo(() => role === 'locum', [role]);
  const isPharmacist = useMemo(
    () => isLocum && locumRole === 'pharmacist',
    [isLocum, locumRole]
  );
  const isTechnician = useMemo(
    () => isLocum && locumRole === 'technician',
    [isLocum, locumRole]
  );
  const termsUrl = isPharmacist ? PHARMACIST_TERMS_URL : LOCUM_TERMS_URL;
  const privacyUrl = isPharmacist ? PHARMACIST_PRIVACY_URL : LOCUM_PRIVACY_URL;

  useEffect(() => {
    if (errorMessage || successMessage) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [errorMessage, successMessage]);

  const handleDocumentFileChange = (docKey: DocumentKey, file: File | null) => {
    setDocumentFiles(prev => ({
      ...prev,
      [docKey]: file
    }));
  };

  const handleAddQualificationCertificate = (file: File | null) => {
    if (file && qualificationCertificates.length < 4) {
      setQualificationCertificates(prev => [...prev, file]);
    }
  };

  const handleRemoveQualificationCertificate = (index: number) => {
    setQualificationCertificates(prev =>
      prev.filter((_, i) => i !== index)
    );
  };

  const getCurrentPosition = () =>
    new Promise<GeolocationPosition>((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by this browser'));
        return;
      }

      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 10000
      });
    });

  const handleUseCurrentLocation = async () => {
    setErrorMessage('');
    setIsDetectingLocation(true);

    try {
      const position = await getCurrentPosition();
      const coords = {
        lat: position.coords.latitude,
        lng: position.coords.longitude
      };

      setDetectedCoordinates(coords);

      try {
        const formattedAddress = await reverseGeocode(coords);
        if (formattedAddress) {
          setLocation(formattedAddress);
        }
      } catch {
        // Ignore reverse geocode failure (e.g. missing Google API key) during testing.
      }
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : 'Unable to detect your current location'
      );
    } finally {
      setIsDetectingLocation(false);
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');
    setIsLoading(true);

    try {
      const normalizedPhone = isLocum ? normalizeUkPhoneLocal(phone) : '';
      const normalizedPharmacyPhone = !isLocum
        ? normalizeUkPhoneLocal(pharmacyPhone)
        : '';
      const normalizedRefPhone1 = normalizeUkPhoneLocal(refPhone1);
      const normalizedRefPhone2 = normalizeUkPhoneLocal(refPhone2);
      const trimmedAddress = address.trim();
      const trimmedCity = city.trim();
      const trimmedZipCode = zipCode.trim();
      const trimmedNationalInsuranceNumber = nationalInsuranceNumber.trim();
      const trimmedRefName1 = refName1.trim();
      const trimmedRefDetails1 = refDetails1.trim();
      const trimmedRefName2 = refName2.trim();
      const trimmedRefDetails2 = refDetails2.trim();

      if (isLocum && !normalizedPhone) {
        setErrorMessage('Please enter a phone number');
        setIsLoading(false);
        return;
      }

      if (isLocum && normalizedPhone && !UK_PHONE_LOCAL_REGEX.test(normalizedPhone)) {
        setErrorMessage('Please enter a valid UK phone number');
        setIsLoading(false);
        return;
      }

      if (!isLocum && !UK_PHONE_LOCAL_REGEX.test(normalizedPharmacyPhone)) {
        setErrorMessage('Please enter a valid pharmacy phone number');
        setIsLoading(false);
        return;
      }

      if (isLocum && normalizedRefPhone1 && !UK_PHONE_LOCAL_REGEX.test(normalizedRefPhone1)) {
        setErrorMessage('Reference 1 phone must be a valid UK number');
        setIsLoading(false);
        return;
      }

      if (isLocum && normalizedRefPhone2 && !UK_PHONE_LOCAL_REGEX.test(normalizedRefPhone2)) {
        setErrorMessage('Reference 2 phone must be a valid UK number');
        setIsLoading(false);
        return;
      }

      if (isLocum && normalizedPhone !== phone) {
        setPhone(normalizedPhone);
      }

      if (!isLocum && normalizedPharmacyPhone !== pharmacyPhone) {
        setPharmacyPhone(normalizedPharmacyPhone);
      }

      if (normalizedRefPhone1 !== refPhone1) {
        setRefPhone1(normalizedRefPhone1);
      }

      if (normalizedRefPhone2 !== refPhone2) {
        setRefPhone2(normalizedRefPhone2);
      }

      // Validate passwords match
      if (password !== confirmPassword) {
        setErrorMessage('Passwords do not match');
        setIsLoading(false);
        return;
      }

      // Validate location is selected (only for locums)
      if (isLocum && !location.trim()) {
        setErrorMessage('Please select a location');
        setIsLoading(false);
        return;
      }

      // New locum fields required
      if (isLocum) {
        if ((isPharmacist || isTechnician) && !gphcNumber.trim()) {
          setErrorMessage('Please enter GPhC Number');
          setIsLoading(false);
          return;
        }
        if (!trimmedAddress) {
          setErrorMessage('Please enter address');
          setIsLoading(false);
          return;
        }
        if (!trimmedCity) {
          setErrorMessage('Please enter city');
          setIsLoading(false);
          return;
        }
        if (!trimmedZipCode) {
          setErrorMessage('Please enter postcode');
          setIsLoading(false);
          return;
        }
        if (!trimmedNationalInsuranceNumber) {
          setErrorMessage('Please enter National Insurance Number');
          setIsLoading(false);
          return;
        }
        if (!trimmedRefName1) {
          setErrorMessage('Please enter Reference 1 name');
          setIsLoading(false);
          return;
        }
        if (!normalizedRefPhone1) {
          setErrorMessage('Please enter Reference 1 phone');
          setIsLoading(false);
          return;
        }
        if (!trimmedRefDetails1) {
          setErrorMessage('Please enter Reference 1 details');
          setIsLoading(false);
          return;
        }
        if (!trimmedRefName2) {
          setErrorMessage('Please enter Reference 2 name');
          setIsLoading(false);
          return;
        }
        if (!normalizedRefPhone2) {
          setErrorMessage('Please enter Reference 2 phone');
          setIsLoading(false);
          return;
        }
        if (!trimmedRefDetails2) {
          setErrorMessage('Please enter Reference 2 details');
          setIsLoading(false);
          return;
        }
        if (!dob) {
          setErrorMessage('Please enter date of birth');
          setIsLoading(false);
          return;
        }
        if (!gender) {
          setErrorMessage('Please select gender');
          setIsLoading(false);
          return;
        }
        if (!qualificationDate) {
          setErrorMessage('Please enter qualification date');
          setIsLoading(false);
          return;
        }
        if (!acceptedIr35Compliance) {
          setErrorMessage(
            'Please confirm IR35 compliance and responsibility for your own Tax and National Insurance Contributions'
          );
          setIsLoading(false);
          return;
        }
        if (isPharmacist && independentPrescriber === '') {
          setErrorMessage('Please indicate whether you are an independent prescriber');
          setIsLoading(false);
          return;
        }
        if (!acceptedTerms) {
          setErrorMessage('Please confirm you have read and agree to the terms and conditions');
          setIsLoading(false);
          return;
        }
        if (!acceptedPrivacy) {
          setErrorMessage('Please confirm you have read and agree to the privacy policy');
          setIsLoading(false);
          return;
        }

        const missingLocumDocument = LOCUM_DOCUMENT_FIELDS.find(field => {
          const isFieldRequired =
            field.key === 'safeguarding_level_2' ? isPharmacist : field.required;

          if (!isFieldRequired) {
            return false;
          }

          if (field.key === 'qualification_certificates') {
            return qualificationCertificates.length === 0;
          }

          return !documentFiles[field.key];
        });

        if (missingLocumDocument) {
          setErrorMessage(`${missingLocumDocument.label} document is required`);
          setIsLoading(false);
          return;
        }

        if (isPharmacist) {
          const missingPharmacistDocument = PHARMACIST_LOCUM_DOCUMENTS.find(
            field => field.required && !documentFiles[field.key]
          );

          if (missingPharmacistDocument) {
            setErrorMessage(`${missingPharmacistDocument.label} document is required`);
            setIsLoading(false);
            return;
          }
        }
      } else {
        if (!pharmacyAddress.trim()) {
          setErrorMessage('Please enter pharmacy address');
          setIsLoading(false);
          return;
        }
        if (!dispensingSystem.trim()) {
          setErrorMessage('Please enter dispensing system');
          setIsLoading(false);
          return;
        }
      }

      // Validate coordinates are captured (only for locums)
      if (
        isLocum &&
        (!detectedCoordinates ||
          !detectedCoordinates.lat ||
          !detectedCoordinates.lng)
      ) {
        setErrorMessage('Please select a valid location from the suggestions');
        setIsLoading(false);
        return;
      }

      const resolvedLocation = isLocum ? location.trim() : '';
      const resolvedCoordinates = detectedCoordinates;

      const payload = new FormData();

      payload.append('name', name);
      payload.append('last_name', last_name);
      payload.append('email', email);
      payload.append('password', password);
      payload.append('role', role);

      if (isLocum) {
        payload.append('ref_Name_1', trimmedRefName1);
        payload.append('ref_PhoneNumber_1', formatUkPhone(normalizedRefPhone1));
        payload.append('ref_Details_1', trimmedRefDetails1);
        payload.append('ref_Name_2', trimmedRefName2);
        payload.append('ref_PhoneNumber_2', formatUkPhone(normalizedRefPhone2));
        payload.append('ref_Details_2', trimmedRefDetails2);
        payload.append('location', resolvedLocation);
        payload.append('latitude', String(resolvedCoordinates!.lat));
        payload.append('longitude', String(resolvedCoordinates!.lng));
        payload.append('phone', formatUkPhone(normalizedPhone));
        payload.append('qualifications', qualifications);
        payload.append('experience_years', String(Number(experienceYears)));
        payload.append('travel_distance', String(Number(travelDistance)));
        payload.append('locum_role', locumRole);
        payload.append('gphc_number', gphcNumber);
        payload.append('national_insurance_number', trimmedNationalInsuranceNumber);
        payload.append('address', trimmedAddress);
        payload.append('city', trimmedCity);
        payload.append('zip_code', trimmedZipCode);
        payload.append('dob', dob);
        payload.append('gender', gender);
        payload.append('qualification_date', qualificationDate);
        // independentPrescriber is '1' for yes, '0' for no, or '' if not selected
        payload.append('independent_prescriber', isPharmacist ? (independentPrescriber || '') : '0');

        // Add locum documents
        for (const field of LOCUM_DOCUMENT_FIELDS) {
          if (field.key === 'qualification_certificates') {
            // Add all qualification certificates
            qualificationCertificates.forEach(file => {
              payload.append(field.key, file);
            });
          } else {
            const file = documentFiles[field.key];
            if (file) {
              payload.append(field.key, file);
            }
          }
        }

        // Add pharmacist-specific documents if applicable
        if (isPharmacist) {
          for (const field of PHARMACIST_LOCUM_DOCUMENTS) {
            const file = documentFiles[field.key];
            if (file) {
              payload.append(field.key, file);
            }
          }
        }
      } else {
        // Pharmacy role - location can be added later in profile page
        payload.append('pharmacy_name', pharmacyName);
        payload.append('contact_person', contactPerson);
        payload.append('phone_number', formatUkPhone(normalizedPharmacyPhone));
        payload.append('address', pharmacyAddress);
        payload.append('dispensing_system', dispensingSystem);
        payload.append('license_number', licenseNumber);
      }

      await register(payload);
      setSuccessMessage('Registration successful! Redirecting to login...');
      setTimeout(() => {
        navigate('/auth/login', { replace: true });
      }, 1500);
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'Register failed'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthSimpleLayout className="col-xl-10 col-xxl-9">
      <div className="mx-auto" style={{ maxWidth: '1240px' }}>
        <div className="text-center mb-6">
          <h3 className="text-body-highlight">Register</h3>
        </div>

        {errorMessage && (
          <Alert
            variant="subtle-danger"
            className="mb-4"
            dismissible
            onClose={() => setErrorMessage('')}
          >
            {errorMessage}
          </Alert>
        )}
        {successMessage && (
          <Alert
            variant="subtle-success"
            className="mb-4"
            dismissible
            onClose={() => setSuccessMessage('')}
          >
            {successMessage}
          </Alert>
        )}

        <Form onSubmit={handleSubmit} noValidate>
          <Row className="g-3">
            <Col md={6} lg={3}>
              <Form.Group className="text-start">
                <Form.Label>
                  First Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Enter First name"
                  required
                />
              </Form.Group>
            </Col>
             <Col md={6} lg={3}>
              <Form.Group className="text-start">
                <Form.Label>
                  Last Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  value={last_name}
                  onChange={e => setLastName(e.target.value)}
                  placeholder="Enter Last name"
                  required
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="text-start">
                <Form.Label>
                  Email <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="name@example.com"
                  required
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="text-start">
                <Form.Label>
                  Password <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Strong password"
                  required
                />
              </Form.Group>
              {password && <PasswordCriteria password={password} />}
            </Col>

            <Col md={6}>
              <Form.Group className="text-start">
                <Form.Label>
                  Confirm Password <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="Confirm password"
                  required
                  isInvalid={confirmPassword !== '' && password !== confirmPassword}
                />
                {confirmPassword !== '' && password !== confirmPassword && (
                  <Form.Control.Feedback type="invalid">
                    Passwords do not match
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="text-start">
                <Form.Label>
                  Role <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  value={role}
                  onChange={e => setRole(e.target.value as UserRole)}
                >
                  <option value="locum">Locum</option>
                  <option value="pharmacy">Pharmacy</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col xs={12}>
              {isLocum && (
                <LocationAutocomplete
                  value={location}
                  onChange={setLocation}
                  onCoordinatesChange={setDetectedCoordinates}
                  label="Location"
                  placeholder="Search by address / area"
                  required
                />
              )}
            </Col>
          </Row>

          {isLocum ? (
            <>
              <Row className="g-3 mt-1">
                <Col md={6}>
                  <Form.Group className="text-start">
                    <Form.Label>Phone <span className="text-danger">*</span></Form.Label>
                    <InputGroup>
                      <InputGroup.Text>+44</InputGroup.Text>
                      <Form.Control
                        type="tel"
                        value={phone}
                        onChange={e =>
                          setPhone(normalizeUkPhoneLocal(e.target.value))
                        }
                        placeholder="7XXXXXXXXX"
                        inputMode="numeric"
                        pattern="\\d{9,10}"
                        title="Enter 9-10 digits after +44"
                        required
                      />
                    </InputGroup>
                    <Form.Text className="text-body-tertiary">
                      Enter 9-10 digits after the +44 prefix.
                    </Form.Text>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="text-start">
                    <Form.Label>Qualifications</Form.Label>
                    <Form.Control
                      value={qualifications}
                      onChange={e => setQualifications(e.target.value)}
                      placeholder="PharmD, B.Pharm, etc."
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="text-start">
                    <Form.Label>
                      Experience Years <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="number"
                      min={0}
                      value={experienceYears}
                      onChange={e => setExperienceYears(e.target.value)}
                      placeholder="Enter experience in years"
                      required
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="text-start">
                    <Form.Label>
                      Travel Distance (miles){' '}
                      <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="number"
                      min={0}
                      value={travelDistance}
                      onChange={e => setTravelDistance(e.target.value)}
                      placeholder="Enter travel distance"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="text-start">
                    <Form.Label>
                      Your Role <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select
                      value={locumRole}
                      onChange={e =>
                        setLocumRole(
                          e.target.value as 'pharmacist' | 'technician' | 'dispenser'
                        )
                      }
                      required
                    >
                      <option value="pharmacist">Pharmacist</option>
                      <option value="technician">Technician</option>
                      <option value="dispenser">Dispenser</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="text-start">
                    <Form.Label>
                      GPhC Number (7 Digits){' '}
                      {(isPharmacist || isTechnician) && (
                        <span className="text-danger">*</span>
                      )}
                    </Form.Label>

                    <Form.Control
                      value={gphcNumber}
                      onChange={e => setGphcNumber(e.target.value)}
                      placeholder="Enter GPhC Number"
                      required={isPharmacist || isTechnician}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="text-start">
                    <Form.Label>
                      Address <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      value={address}
                      onChange={e => setAddress(e.target.value)}
                      placeholder="Street address"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="text-start">
                    <Form.Label>
                      National Insurance Number <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      value={nationalInsuranceNumber}
                      onChange={e => setNationalInsuranceNumber(e.target.value)}
                      placeholder="Enter National Insurance Number"
                      required
                    />
                  </Form.Group>
                </Col>
                

                <Col md={4}>
                  <Form.Group className="text-start">
                    <Form.Label>
                      City <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      value={city}
                      onChange={e => setCity(e.target.value)}
                      placeholder="City"
                      required
                    />
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="text-start">
                    <Form.Label>
                      Postcode <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      value={zipCode}
                      onChange={e => setZipCode(e.target.value)}
                      placeholder="Postcode"
                      required
                    />
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="text-start">
                    <Form.Label>Date of Birth <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="date"
                      value={dob}
                      onChange={e => setDob(e.target.value)}
                      required
                    />
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="text-start">
                    <Form.Label>Gender <span className="text-danger">*</span></Form.Label>
                    <Form.Select
                      value={gender}
                      onChange={e =>
                        setGender(e.target.value as 'male' | 'female' | 'other' | '')
                      }
                      required
                    >
                      <option value="">Select gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="text-start">
                    <Form.Label>Qualification Date <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="date"
                      value={qualificationDate}
                      onChange={e => setQualificationDate(e.target.value)}
                      required
                    />
                  </Form.Group>
                </Col>

                {isPharmacist && (
                  <Col md={4}>
                    <Form.Group className="text-start">
                      <Form.Label>Independent Prescriber <span className="text-danger">*</span></Form.Label>
                      <div className="d-flex gap-3 align-items-center mt-2">
                        <Form.Check
                          type="checkbox"
                          id="independent-prescriber-yes"
                          label="Yes"
                          checked={independentPrescriber === '1'}
                          onChange={e => setIndependentPrescriber(e.target.checked ? '1' : '')}
                        />
                        <Form.Check
                          type="checkbox"
                          id="independent-prescriber-no"
                          label="No"
                          checked={independentPrescriber === '0'}
                          onChange={e => setIndependentPrescriber(e.target.checked ? '0' : '')}
                        />
                      </div>
                    </Form.Group>
                  </Col>
                )}
              </Row>

              {isLocum && (
                <div className="border rounded-3 p-3 mt-4 bg-body-emphasis">
                  <h5 className="mb-2 text-body-highlight">
                    Professional references
                  </h5>
                  <p className="text-body-tertiary fs-9 mb-3">
                    Please provide both professional references.
                  </p>

                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group className="text-start">
                        <Form.Label>
                          Reference 1 Name <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          value={refName1}
                          onChange={e => setRefName1(e.target.value)}
                          placeholder="Enter name"
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="text-start">
                        <Form.Label>
                          Reference 1 Phone <span className="text-danger">*</span>
                        </Form.Label>
                        <InputGroup>
                          <InputGroup.Text>+44</InputGroup.Text>
                          <Form.Control
                            type="tel"
                            value={refPhone1}
                            onChange={e =>
                              setRefPhone1(normalizeUkPhoneLocal(e.target.value))
                            }
                            placeholder="7XXXXXXXXX"
                            inputMode="numeric"
                            pattern="\\d{9,10}"
                            title="Enter 9-10 digits after +44"
                            required
                          />
                        </InputGroup>
                      </Form.Group>
                    </Col>
                    <Col xs={12}>
                      <Form.Group className="text-start">
                        <Form.Label>
                          Reference 1 Details <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          value={refDetails1}
                          onChange={e => setRefDetails1(e.target.value)}
                          placeholder="Relationship, role, company, etc."
                          required
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group className="text-start">
                        <Form.Label>
                          Reference 2 Name <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          value={refName2}
                          onChange={e => setRefName2(e.target.value)}
                          placeholder="Enter name"
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="text-start">
                        <Form.Label>
                          Reference 2 Phone <span className="text-danger">*</span>
                        </Form.Label>
                        <InputGroup>
                          <InputGroup.Text>+44</InputGroup.Text>
                          <Form.Control
                            type="tel"
                            value={refPhone2}
                            onChange={e =>
                              setRefPhone2(normalizeUkPhoneLocal(e.target.value))
                            }
                            placeholder="7XXXXXXXXX"
                            inputMode="numeric"
                            pattern="\\d{9,10}"
                            title="Enter 9-10 digits after +44"
                            required
                          />
                        </InputGroup>
                      </Form.Group>
                    </Col>
                    <Col xs={12}>
                      <Form.Group className="text-start">
                        <Form.Label>
                          Reference 2 Details <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          value={refDetails2}
                          onChange={e => setRefDetails2(e.target.value)}
                          placeholder="Relationship, role, company, etc."
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              )}

              <div className="border rounded-3 p-3 mt-4 bg-body-emphasis">
                <h5 className="mb-2 text-body-highlight">Locum documents</h5>
                <p className="text-body-tertiary fs-9 mb-3">
                  Required uploads except Passport and Visa/Work permit. Safeguarding level 2 is required for pharmacists only. Accepted format: Word, PDF, JPG, PNG.
                </p>

                <Row className="g-3">
                  {LOCUM_DOCUMENT_FIELDS.map(field => {
                    const isFieldRequired =
                      field.key === 'safeguarding_level_2'
                        ? isPharmacist
                        : field.required;

                    return (
                      <Col xs={12} md={6} key={field.key}>
                        <Form.Group className="text-start border rounded-2 p-3 h-100 bg-body">
                          <Form.Label className="mb-1 d-block">
                            {renderDocumentLabel({ ...field, required: isFieldRequired })}
                          </Form.Label>
                          {field.key === 'qualification_certificates' ? (
                            <>
                              <Form.Control
                                type="file"
                                accept=".pdf,.doc,.docx,image/*"
                                onChange={e =>
                                  handleAddQualificationCertificate(
                                    (e.target as HTMLInputElement).files?.[0] ?? null
                                  )
                                }
                                required={
                                  isFieldRequired && qualificationCertificates.length === 0
                                }
                                disabled={qualificationCertificates.length >= 4}
                              />
                              <Form.Text className="text-body-tertiary d-block mt-2">
                                {qualificationCertificates.length} / 4 files uploaded
                              </Form.Text>
                              {qualificationCertificates.length > 0 && (
                                <div className="mt-3">
                                  <h6 className="mb-2 text-body-highlight">Uploaded Files:</h6>
                                  <ul className="list-unstyled">
                                    {qualificationCertificates.map((file, index) => (
                                      <li
                                        key={index}
                                        className="d-flex justify-content-between align-items-center mb-2 p-2 bg-body-secondary rounded"
                                      >
                                        <span className="text-body-tertiary fs-9">
                                          {file.name}
                                        </span>
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-outline-danger"
                                          onClick={() =>
                                            handleRemoveQualificationCertificate(index)
                                          }
                                        >
                                          Remove
                                        </button>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </>
                          ) : (
                            <>
                              <Form.Control
                                type="file"
                                accept=".pdf,.doc,.docx,image/*"
                                onChange={e =>
                                  handleDocumentFileChange(
                                    field.key,
                                    (e.target as HTMLInputElement).files?.[0] ?? null
                                  )
                                }
                                required={isFieldRequired}
                              />
                              {documentFiles[field.key] && (
                                <Form.Text className="text-body-tertiary">
                                  Selected: {documentFiles[field.key]?.name}
                                </Form.Text>
                              )}
                            </>
                          )}
                        </Form.Group>
                      </Col>
                    );
                  })}
                </Row>
                {isPharmacist && <Row className="g-3 mt-1">
                  {PHARMACIST_LOCUM_DOCUMENTS.map(field => (
                    <Col xs={12} md={6} key={field.key}>
                      <Form.Group className="text-start border rounded-2 p-3 h-100 bg-body">
                        <Form.Label className="mb-1 d-block">
                          {field.label}{' '}
                          {field.required && <span className="text-danger">*</span>}
                        </Form.Label>
                        <Form.Control
                          type="file"
                          accept=".pdf,.doc,.docx,image/*"
                          onChange={e =>
                            handleDocumentFileChange(
                              field.key,
                              (e.target as HTMLInputElement).files?.[0] ?? null
                            )
                          }
                          required={field.required}
                        />
                        {documentFiles[field.key] && (
                          <Form.Text className="text-body-tertiary">
                            Selected: {documentFiles[field.key]?.name}
                          </Form.Text>
                        )}
                      </Form.Group>
                    </Col>
                  ))}
                </Row>}
              </div>

              <div className="border rounded-3 p-3 mt-4 bg-body-emphasis">
                <h5 className="mb-2 text-body-highlight">IR35 compliance <span className="text-danger">*</span></h5>
                <div className="text-start border rounded-2 p-3 bg-body">
                  <Form.Check
                    type="checkbox"
                    id="accepted-ir35-compliance"
                    label={
                      <span>
                        I confirm that I am IR35 compliant and responsible for my own Tax and
                        National Insurance Contributions. <span className="text-danger">*</span>
                      </span>
                    }
                    checked={acceptedIr35Compliance}
                    onChange={e => setAcceptedIr35Compliance(e.target.checked)}
                    required
                  />
                </div>
              </div>

              <div className="border rounded-3 p-3 mt-4 bg-body-emphasis">
                <h5 className="mb-2 text-body-highlight">
                  {isPharmacist ? 'Pharmacist T&Cs' : 'Dispenser/Technician T&Cs'}<span className="text-danger">*</span>
                </h5>
                <p className="text-body-tertiary fs-9 mb-3">
                  Please check each box to confirm that you have read and agree to the{' '}
                  <a href={termsUrl} target="_blank" rel="noreferrer">
                    Capital Locum {isPharmacist ? 'pharmacist' : 'dispenser/technician'} terms and conditions
                  </a>
                  {' '}and{' '}
                  <a href={privacyUrl} target="_blank" rel="noreferrer">
                    Capital Locum data and privacy policy
                  </a>
                  .
                </p>

                <Row className="g-3">
                  <Col md={6}>
                    <Form.Group className="text-start border rounded-2 p-3 h-100 bg-body">
                      <Form.Check
                        type="checkbox"
                        id="accepted-terms"
                        label={
                          <span>
                            I agree to the{' '}
                            <a href={termsUrl} target="_blank" rel="noreferrer">
                              {isPharmacist ? 'Pharmacist' : 'Dispenser/Technician'} T&amp;Cs
                            </a>
                            .<span className="text-danger">*</span>
                          </span>
                        }
                        checked={acceptedTerms}
                        onChange={e => setAcceptedTerms(e.target.checked)}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="text-start border rounded-2 p-3 h-100 bg-body">
                      <Form.Check
                        type="checkbox"
                        id="accepted-privacy"
                        label={
                          <span>
                            I agree to the{' '}
                            <a href={privacyUrl} target="_blank" rel="noreferrer">
                              Privacy Policy
                            </a>
                            .<span className="text-danger">*</span>
                          </span>
                        }
                        checked={acceptedPrivacy}
                        onChange={e => setAcceptedPrivacy(e.target.checked)}
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>


            </>
          ) : (
            <>
              <Row className="g-3 mt-1">
                <Col md={6}>
                  <Form.Group className="text-start">
                    <Form.Label>
                      Pharmacy Name <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      value={pharmacyName}
                      onChange={e => setPharmacyName(e.target.value)}
                      placeholder="Enter pharmacy name"
                      required
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="text-start">
                    <Form.Label>
                      Contact Person <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      value={contactPerson}
                      onChange={e => setContactPerson(e.target.value)}
                      placeholder="Owner / Manager name"
                      required
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="text-start">
                    <Form.Label>
                      Pharmacy Phone <span className="text-danger">*</span>
                    </Form.Label>
                    <InputGroup>
                      <InputGroup.Text>+44</InputGroup.Text>
                      <Form.Control
                        type="tel"
                        value={pharmacyPhone}
                        onChange={e =>
                          setPharmacyPhone(normalizeUkPhoneLocal(e.target.value))
                        }
                        placeholder="7XXXXXXXXX"
                        inputMode="numeric"
                        pattern="\\d{9,10}"
                        title="Enter 9-10 digits after +44"
                        required
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="text-start">
                    <Form.Label>
                      Dispensing System <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      value={dispensingSystem}
                      onChange={e => setDispensingSystem(e.target.value)}
                      placeholder="ProScript Connect, EPS, etc."
                      required
                    />
                  </Form.Group>
                </Col>

                <Col xs={12}>
                  <Form.Group className="text-start">
                    <Form.Label>
                      Pharmacy Address <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      value={pharmacyAddress}
                      onChange={e => setPharmacyAddress(e.target.value)}
                      placeholder="Full address"
                      required
                    />
                  </Form.Group>
                </Col>

                <Col xs={12}>
                  <Form.Group className="text-start">
                    <Form.Label>License Number</Form.Label>
                    <Form.Control
                      value={licenseNumber}
                      onChange={e => setLicenseNumber(e.target.value)}
                      placeholder="Optional"
                    />
                  </Form.Group>
                </Col>
              </Row>

            </>
          )}

          <Button
            type="submit"
            variant="primary"
            className="w-100 mb-3 mt-4"
            disabled={
              isLoading ||
              isDetectingLocation ||
              (isLocum && (!acceptedIr35Compliance || !acceptedTerms || !acceptedPrivacy))
            }
          >
            {isLoading ? 'Registering...' : 'Register'}
          </Button>

          <div className="text-center fs-9">
            Already have an account? <Link to="/auth/login">Login</Link>
          </div>
        </Form>
      </div>
    </AuthSimpleLayout>
  );
};

export default RegisterPage;
